import React, { useMemo, useState } from 'react';
import { freedomInitialFeeWarning, freedomPeriodWarning } from '../../../constants/bankConditions';
import styles from './deal.module.css';
import SelectCreditPeriod, { PERIODS } from '../../../components/Selects/SelectCreditPeriod';
import createNumberMask from '../../../utils/createNumberMask';
import HookForm from '../../../components/HookForm/HookForm';
import SelectFamilyStatus, { MARITAL_STATUSES } from '../../../components/Selects/SelectFamilyStatus';
import DealCreateDocs from './DealCreateDocs';
import {
  CLIENT_CONTACT_PERSON_TYPES,
  CLIENT_DOCUMENT_ISSUED_BY,
  DOCUMENT_TYPES_PERSONAL_DATA_AGREEMENT,
  IS_PROD_STAND,
  MARITAL_STATUS_MARRIED,
  RESIDENT_TYPES,
  USED_CAR_BANK_IDS,
} from '../../../constants';
import Modal from '../../../components/Modal/Modal';
import Button from '../../../components/Button/Button';
import DealCreateAgreement from './DealCreateAgreement';
import { MASK } from '../../../constants/mask';
import DocsInputNew from '../../../components/Inputs/DocsInput/DocsInputNew';
import alert from '../../../helpers/alert';
import { textStyles } from '../../../constants/textStyles';
import Hint from '../../../components/Hint';
import numberMask from '../../../utils/numberMask';
import Checkbox from '../../../components/Inputs/Checkbox/Checkbox';
import SelectContactType from '../../../components/Selects/SelectContactType';
import SelectResitentType from '../../../components/Selects/SelectResitentType';
import SelectClientDocumentType from '../../../components/Selects/SelectClientDocumentTypes';
import SelectClientIssuer from '../../../components/Selects/SelectClientDocumentType';
import SelectWorkStatus, { WORK_STATUSES } from '../../../components/Selects/SelectWorkStatus';
import BanksPicker from '../../../components/Credit/BanksPicker';
import Select from '../../../components/Select/Select';
import { shouldClearDraft } from '../../../utils/appealSubmitState';
import getClients from '../../../api/clients/getClients';
import ClientUploadBlock, { useClientUploads } from '../../../components/Credit/ClientUploadBlock';
import { hasDocument } from '../../Public/clientUploadKinds';
import deleteClientUploadFile from '../../../api/clientUpload/deleteClientUploadFile';
import { flagFromDraft, repaymentPayload, withoutRepaymentFields } from './greyAppeal';
import { shortFormsEnabled } from '../../../brand/tenant';
import { loadClientPhoto, loadIdentityScans } from './identityScans';

const CREDIT_TYPE_OPTIONS = [
  {
    label: 'Aннуитетный',
    value: 0,
  },
  {
    label: 'Дифференцированный',
    value: 1,
  },
];

const DRAFT_STORAGE_PREFIX = 'dealCreateCreditAppealDraft';

const isBrowserStorageAvailable = () => typeof window !== 'undefined' && !!window.localStorage;

const readDraft = (key) => {
  if (!key || !isBrowserStorageAvailable()) return {};

  try {
    return JSON.parse(window.localStorage.getItem(key)) || {};
  } catch (e) {
    return {};
  }
};

const writeDraft = (key, draft) => {
  if (!key || !isBrowserStorageAvailable()) return;

  try {
    window.localStorage.setItem(key, JSON.stringify(draft));
  } catch (e) {}
};

const removeDraft = (key) => {
  if (!key || !isBrowserStorageAvailable()) return;
  window.localStorage.removeItem(key);
};

const findOption = (options, value) => {
  if (value && typeof value === 'object' && 'value' in value) return value;
  return options.find((option) => String(option.value) === String(value));
};

const normalizeFormValues = (values = {}) => {
  const normalized = { ...values };

  normalized.repaymentType = findOption(CREDIT_TYPE_OPTIONS, values.repaymentType);
  normalized.period = findOption(PERIODS, values.period);
  normalized.MaritalStatus = findOption(MARITAL_STATUSES, values.MaritalStatus);
  normalized.workStatus = findOption(WORK_STATUSES, values.workStatus?.value || values.workStatusId || values.workStatus);
  normalized.contactPersonType = findOption(CLIENT_CONTACT_PERSON_TYPES, values.contactPersonType);
  normalized.contactPersonType2 = findOption(CLIENT_CONTACT_PERSON_TYPES, values.contactPersonType2);
  normalized.residentBool = findOption(RESIDENT_TYPES, values.residentBool);
  normalized.residentBool2 = findOption(RESIDENT_TYPES, values.residentBool2);
  normalized.documentGivedBy = findOption(CLIENT_DOCUMENT_ISSUED_BY, values.documentGivedBy);
  normalized.documentGivedBy2 = findOption(CLIENT_DOCUMENT_ISSUED_BY, values.documentGivedBy2);

  return normalized;
};

const DealCreateCreditAppeal = ({
  deal,
  documents,
  onSubmit,
  orders,
  agreementModal,
  setAgreementModal,
  backHandler,
  sendFormLoading,
  defaultValues,
  udvIsRequired = true,
  usedCar = false,
}) => {
  const draftStorageKey = React.useMemo(() => {
    const dealKey = deal?.chunkId || deal?.id;
    return dealKey ? `${DRAFT_STORAGE_PREFIX}:${dealKey}` : null;
  }, [deal?.chunkId, deal?.id]);
  const savedDraft = React.useMemo(() => readDraft(draftStorageKey), [draftStorageKey]);
  const restoredValues = React.useMemo(
    () => normalizeFormValues({ ...(defaultValues || {}), ...(savedDraft?.values || {}) }),
    [defaultValues, savedDraft]
  );
  const saveDraftTimeoutRef = React.useRef(null);
  const latestValuesRef = React.useRef(restoredValues);

  // Без данных удостоверяющего документа заявку отправлять некуда: Шинхан на
  // таких клиентах падал (сделки 189083, 189221, 189240), остальные банки
  // отказывают. Карточку клиента можно завести и без документа — например,
  // из мобильного приложения, — поэтому проверяем перед отправкой.
  const [clientDocumentMissing, setClientDocumentMissing] = React.useState(false);
  const [clientPhotoUrl, setClientPhotoUrl] = React.useState('');

  React.useEffect(() => {
    const clientId = deal?.client?.id;
    if (!clientId) return;

    getClients({ id: clientId })
      .then((res) => {
        const client = res?.data?.users?.[0];
        setClientDocumentMissing(!client?.document?.document_number);
        setClientPhotoUrl(client?.photo || '');
      })
      .catch(() => setClientDocumentMissing(false));
  }, [deal?.client?.id]);

  const [files, setFiles] = React.useState({
    UDV: null,
    passportRK: null,
    clientPhoto: null,
    incomeCertificate: null,
    workCertificate: null,
    legalConfirm: null,
    vinApprovalDocument: null,
  });

  // Скан удостоверения клиента уже может лежать в его карточке (его кладёт ПКБ при добавлении
  // через eGov) — подставляем в «УДВ» и «Паспорт РК», чтобы не грузить заново. Руками выбранное не трогаем.
  React.useEffect(() => {
    const clientId = deal?.client?.id;
    if (!clientId) return undefined;
    let cancelled = false;
    loadIdentityScans(clientId)
      .then((found) => {
        if (cancelled) return;
        setFiles((prev) => ({
          ...prev,
          UDV: prev.UDV || found.UDV || null,
          passportRK: prev.passportRK || found.passportRK || null,
        }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [deal?.client?.id]);

  // Фотография клиента лежит не в документах, а в аватаре карточки — её тоже кладёт
  // ПКБ (снимок с удостоверения). Банки требуют фото в анкете, и менеджер искал его
  // сам, хотя оно уже есть.
  React.useEffect(() => {
    if (!clientPhotoUrl) return undefined;
    let cancelled = false;
    loadClientPhoto(clientPhotoUrl).then((photo) => {
      if (cancelled || !photo) return;
      setFiles((prev) => ({ ...prev, clientPhoto: prev.clientPhoto || photo }));
    });
    return () => {
      cancelled = true;
    };
  }, [clientPhotoUrl]);

  // const inputs = useMemo(() => {
  const inputs = [
    {
      label: 'Вид платежа',
      name: 'repaymentType',
      component: Select,
      options: CREDIT_TYPE_OPTIONS,
      rules: {
        required: true,
      },
      placeholder: 'Выберите из списка',
    },
    {
      label: 'День погашения',
      name: 'repaymentDay',
      placeholder: 'От 1 до 23',
      inputType: 'number',
      rules: {
        validate: (value) => {
          if (value === undefined || value === null || value === '') return true;
          const num = Number(value);
          if (Number.isNaN(num) || num < 1 || num > 23) {
            return 'Введите число от 1 до 23';
          }
          return true;
        },
      },
    },
    {
      label: 'Первоначальный взнос',
      name: 'initialFee',
      placeholder: 'Введите первоначальный взнос',
      mask: numberMask,
      inputMode: 'numeric',
      hint: 'Для получения положительного ответа и выгодных условий от ForteBank требуется первоначальный взнос не менее 15%.',
      // Freedom отказывает по взносу чаще, чем одобряет заявки: за неделю 203
      // отказа из 937 заявок, почти все — взнос ниже 10%, обычно нулевой.
      // Менеджер узнавал об этом через полчаса из карточки.
      warning: (values) => freedomInitialFeeWarning(values?.initialFee, deal?.sum),
      rules: {
        valueAsNumber: true,
        validate: (value) => {
          value = value?.replace(/\s/g, '');
          value = parseInt(value);
          console.log(deal?.sum);
          let error = true;
          if (value === 0) {
            return error;
          }
          const carPrice = deal?.sum;
          // if (value < (carPrice * 0.1)?.toFixed(0)) {
          //   error = `Минимальная сумма взноса 10% от стоимости автомобиля`;
          // } else
          if (value > carPrice) {
            error = 'Сумма взноса не может превышать стоимость автомобиля';
          } else {
            error = true;
          }

          return error;
        },
      },
      // defaultValue: defaultValues?.initialFee,
    },
    {
      label: 'Срок кредитования, месяцев',
      name: 'period',
      component: SelectCreditPeriod,
      // На 36, 48 и 24 месяцах Freedom отказывает почти всегда (45 из 48,
      // 37 из 40, 19 из 23 заявок), на 60/72/84 отказов по сроку нет вовсе.
      warning: (values) => freedomPeriodWarning(values?.period),
      rules: {
        required: true,
      },
      // defaultValue: defaultValues?.period,
    },
    {
      label: 'Основной доход',
      name: 'incomeMain',
      rules: {
        required: true,
      },
      mask: numberMask,
      inputMode: 'numeric',
      // defaultValue: defaultValues?.incomeMain,
    },
    // {
    //   label: 'Дополнительный заработок',
    //   name: 'IncomeAddConfirmed',
    //   mask: numberMask,
    // },
    {
      label: 'Место работы',
      name: 'workName',
      rules: {
        required: true,
        validate: (value) => {
          if (value.length > 50) {
            return 'Максимальная допустимая длина поля "Место работы" - 50 символов';
          }

          return true;
        },
      },
      // defaultValue: defaultValues?.workName,
    },
    {
      label: 'Адрес работы',
      name: 'workAddress',
      rules: {
        required: true,
      },
      // defaultValue: defaultValues?.workAddress,
    },
    {
      label: 'Должность',
      name: 'jobTitle',
      rules: {
        required: true,
      },
    },
    // Телефон компании стоит рядом с местом работы: внизу формы, после «Количество
    // детей», менеджеры его пропускали — поле обязательное, и заявка не отправлялась.
    {
      label: 'Номер телефона компании, в которой работаете',
      name: 'companyPhone',

      type: 'phone',
      mask: MASK.PHONE,
      inputMode: 'numeric',

      hint: 'Номер телефона компании нужен только для Евразийского банка',
      rules: {
        required: true,
        validate: (value) => {
          value = value.replace(/[^0-9.]/g, '');
          return value.length === 11;
        },
      },
      // defaultValue: defaultValues?.companyPhone,
    },
    {
      component: SelectWorkStatus,
      name: 'workStatus',
      hint: 'Обязательно для Банк ЦентрКредит',
      // defaultValue: defaultValues?.workStatus,
    },
    {
      label: 'Семейное положение',
      name: 'MaritalStatus',
      component: SelectFamilyStatus,
      rules: {
        required: true,
      },
      // defaultValue: defaultValues?.MaritalStatus,
    },
    {
      label: 'Доход супруги/супруга',
      name: 'SpouseIncomeAmount',
      mask: numberMask,
      inputMode: 'numeric',
      hide: (values) => !(values === MARITAL_STATUS_MARRIED),
      dependency: `MaritalStatus`,
      dependencyAccessor: (obj) => obj?.value,
      // defaultValue: defaultValues?.SpouseIncomeAmount,
    },
    {
      label: 'Количество детей',
      name: 'childrensCount',
      mask: numberMask,
      inputMode: 'numeric',
      // defaultValue: defaultValues?.childrensCount,
      rules: {
        required: true,
      },
    },
    // {
    //   label: 'Страховка для Sapa',
    //   name: 'kaskoInsuranceRate',
    //   component: Select,
    //   options: [
    //     { label: 'KASKO 2.47', value: '1' },
    //     { label: 'KASKO 4.84', value: '2' },
    //     { label: 'Нет', value: null },
    //   ],
    //   rules: {
    //     required: true,
    //   },
    //   // defaultValue: defaultValues?.kaskoInsuranceRate,
    // },
    // {
    //   label: 'Страхование жизни для Sapa',
    //   name: 'lifeInsuranceRate',
    //   component: Select,
    //   options: [
    //     { label: 'Максимум 8', value: '1' },
    //     { label: 'Pro 5.5', value: '3' },
    //     { label: 'Start 4.87', value: '4' },
    //     { label: 'Субсидия 6', value: '6' },
    //     { label: 'Нет', value: null },
    //   ],
    //   rules: {
    //     required: true,
    //   },
    //   // defaultValue: defaultValues?.kaskoInsuranceRate,
    // },
    {
      component: () => {
        return <p style={{ ...textStyles.formTitle, marginTop: 20 }}>Контактное лицо</p>;
      },
      name: 'Контактное лицо',
      hide: (value) => !value,
      dependencyAccessor: (obj) => obj,
      dependency: `workStatus`,
    },
    {
      label: 'Фамилия',
      name: 'contactPersonLastName',
      hide: (value) => !value,
      dependencyAccessor: (obj) => obj,
      dependency: `workStatus`,
      rules: {
        required: true,
      },
      // defaultValue: defaultValues?.contactPersons?.[0]?.contactPersonLastName,
    },
    {
      label: 'Имя',
      name: 'contactPersonFirstName',
      hide: (value) => !value,
      dependencyAccessor: (obj) => obj,
      dependency: `workStatus`,
      rules: {
        required: true,
      },
      // defaultValue: defaultValues?.contactPersons?.[0]?.contactPersonFirstName,
    },
    {
      label: 'Номер телефона',
      name: 'contactPersonPhone',
      type: 'phone',
      mask: MASK.PHONE,
      inputMode: 'numeric',
      hide: (value) => !value,
      dependencyAccessor: (obj) => obj,
      dependency: `workStatus`,
      rules: {
        required: true,
        validate: (value) => {
          value = value.replace(/[^0-9.]/g, '');
          return value.length === 11;
        },
      },
      // defaultValue: defaultValues?.contactPersons?.[0]?.contactPersonPhone,
    },
    {
      component: SelectContactType,
      name: 'contactPersonType',
      hide: (value) => !value,
      dependencyAccessor: (obj) => obj,
      dependency: `workStatus`,
      rules: {
        required: true,
      },
      // defaultValue: defaultValues?.contactPersons?.[0]?.contactPersonType,
    },
    {
      component: SelectResitentType,
      name: 'residentBool',
      hide: (value) => !value,
      dependencyAccessor: (obj) => obj,
      dependency: `workStatus`,
      rules: {
        required: true,
      },
      // defaultValue: defaultValues?.contactPersons?.[0]?.residentBool,
    },
    {
      component: SelectClientIssuer,
      label: 'Орган выдачи документа',
      name: 'documentGivedBy',
      hide: (value) => !value,
      dependencyAccessor: (obj) => obj,
      dependency: `workStatus`,
      rules: {
        required: true,
      },
      // defaultValue: defaultValues?.contactPersons?.[0]?.documentGivedBy,
    },
    // {
    //   label: 'Должность',
    //   name: 'jobTitle',
    //   hide: (value) => !value,
    //   dependencyAccessor: (obj) => obj,
    //   dependency: `workStatus`,
    //   rules: {
    //     required: true,
    //   },
    // },

    {
      component: () => {
        return <p style={{ ...textStyles.formTitle, marginTop: 20 }}>Контактное лицо 2</p>;
      },
      name: 'Контактное лицо2',
      hide: (value) => !value,
      dependencyAccessor: (obj) => obj,
      dependency: `workStatus`,
    },
    {
      label: 'Фамилия',
      name: 'contactPersonLastName2',
      hide: (value) => !value,
      dependencyAccessor: (obj) => obj,
      dependency: `workStatus`,
      rules: {
        required: true,
      },
      // defaultValue: defaultValues?.contactPersons?.[1]?.contactPersonLastName,
    },
    {
      label: 'Имя',
      name: 'contactPersonFirstName2',
      hide: (value) => !value,
      dependencyAccessor: (obj) => obj,
      dependency: `workStatus`,
      rules: {
        required: true,
      },
      // defaultValue: defaultValues?.contactPersons?.[1]?.contactPersonFirstName,
    },
    {
      label: 'Номер телефона',
      name: 'contactPersonPhone2',
      type: 'phone',
      mask: MASK.PHONE,
      inputMode: 'numeric',
      hide: (value) => !value,
      dependencyAccessor: (obj) => obj,
      dependency: `workStatus`,
      rules: {
        required: true,
      },
      // defaultValue: defaultValues?.contactPersons?.[1]?.contactPersonPhone,
    },
    {
      component: SelectContactType,
      name: 'contactPersonType2',
      hide: (value) => !value,
      dependencyAccessor: (obj) => obj,
      dependency: `workStatus`,
      rules: {
        required: true,
      },
      // defaultValue: defaultValues?.contactPersons?.[1]?.contactPersonType,
    },
    {
      component: SelectResitentType,
      name: 'residentBool2',
      hide: (value) => !value,
      dependencyAccessor: (obj) => obj,
      dependency: `workStatus`,
      rules: {
        required: true,
      },
      // defaultValue: defaultValues?.contactPersons?.[1]?.residentBool,
    },
    {
      component: SelectClientIssuer,
      label: 'Орган выдачи документа',
      name: 'documentGivedBy2',
      hide: (value) => !value,
      dependencyAccessor: (obj) => obj,
      dependency: `workStatus`,
      rules: {
        required: true,
      },
      // defaultValue: defaultValues?.contactPersons?.[1]?.documentGivedBy,
    },

    // {
    //   label: 'Должность',
    //   name: 'jobTitle2',
    //   hide: (value) => !value,
    //   dependencyAccessor: (obj) => obj,
    //   dependency: `workStatus`,
    //   rules: {
    //     required: true,
    //   },
    // },
  ];
  // return inputs;
  // }, [defaultValues])

  const attachFile = (file, key) => {
    setFiles((prev) => ({
      ...prev,
      [key]: file,
    }));
  };

  const [documentsForm, setDocumentsForm] = React.useState([]);
  const [subsidy, setSubsidy] = React.useState(flagFromDraft(savedDraft?.meta?.subsidy));
  const [grace, setGrace] = React.useState(flagFromDraft(savedDraft?.meta?.grace));
  // const [incomeConfirmation, setincomeConfirmation] = React.useState(false);
  const [form, setForm] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [selectedBanks, setSelectedBanks] = React.useState(savedDraft?.meta?.selectedBanks);
  const [vehicle_status, setVehicle_status] = React.useState(flagFromDraft(savedDraft?.meta?.vehicle_status));
  const [benefit, setBenefit] = React.useState(flagFromDraft(savedDraft?.meta?.benefit));
  // Тестовый переключатель: помечает заявку как пришедшую из мобильного приложения
  // (createCreditAppealRequest.app -> credit_applications.isMobileApp). Нужен, чтобы
  // на тестовом стенде проверять правило "2 часа без ответа банка", не собирая APK.
  // На проде скрыт (IS_PROD_STAND).
  const [emulateMobileApp, setEmulateMobileApp] = React.useState(false);
  const persistDraft = React.useCallback(
    (values) => {
      if (!draftStorageKey) return;
      latestValuesRef.current = values;

      if (saveDraftTimeoutRef.current) {
        clearTimeout(saveDraftTimeoutRef.current);
      }

      saveDraftTimeoutRef.current = setTimeout(() => {
        writeDraft(draftStorageKey, {
          values,
          meta: {
            subsidy,
            grace,
            selectedBanks,
            vehicle_status,
            benefit,
          },
        });
      }, 300);
    },
    [benefit, draftStorageKey, grace, selectedBanks, subsidy, vehicle_status]
  );

  React.useEffect(() => {
    persistDraft(latestValuesRef.current);

    return () => {
      if (saveDraftTimeoutRef.current) {
        clearTimeout(saveDraftTimeoutRef.current);
      }
    };
  }, [benefit, grace, persistDraft, selectedBanks, subsidy, vehicle_status]);

  const inputsWithDefaultValues = withoutRepaymentFields(inputs).map((input) => ({
    ...input,
    defaultValue: restoredValues[input.name],
  }));

  // Документы, которые клиент прислал сам по ссылке/QR (см. ClientUploadBlock).
  const [clientReceived, refreshClientReceived] = useClientUploads(deal?.id);
  // Считаем внутри customData, а не здесь: HookForm записывает выбранный статус в
  // inputs при своей перерисовке, родитель в этот момент не перерисовывается.
  const statusFlags = () => {
    const workStatusValue = inputsWithDefaultValues?.find((item) => item.name === 'workStatus')?.value?.value;
    return {
      showIncome:
        !!inputsWithDefaultValues?.find((item) => item.name === 'IncomeAddConfirmed')?.value ||
        ['3', '6', '10']?.includes(workStatusValue),
      showWork: workStatusValue == '9',
    };
  };
  // Менеджер убирает присланный клиентом документ; после удаления статус перечитываем.
  const removeClientFile = async (kind) => {
    if (!window.confirm('Удалить документ, который прислал клиент?')) return;
    try {
      await deleteClientUploadFile({ dealId: deal?.id, kind });
      refreshClientReceived();
    } catch (e) {
      // ошибку уже показал plugins/api
    }
  };

  const handleSubmit = (values) => {
    // if (!files['UDV'] && !files['passportRK']) {
    //   return alert.error('Прикрепите один из документов: УДВ или паспорт РК');
    // }

    if (!files['UDV'] && udvIsRequired) {
      return alert.error('Прикрепите документ: УДВ');
    }
    if (!hasDocument(files, clientReceived, 'clientPhoto')) {
      return alert.error('Прикрепите документ: Фотография клиента');
    }

    // if (!files['incomeCertificate']) {
    //   return alert.error('Прикрепите справку о доходах');
    // }
    if (values?.workStatus?.value == '9' && !hasDocument(files, clientReceived, 'workCertificate')) {
      return alert.error('Для статуса клиента "Клиент с выпиской БВУ" обязательный документ "Выписка со счета"');
    }
    if (['3', '6', '10']?.includes(values?.workStatus?.value) && !hasDocument(files, clientReceived, 'incomeCertificate')) {
      return alert.error('Для текущего статуса клиента обязательный документ "Справка о доходах"');
    }

    if (values?.companyPhone?.replace(/[^\d]/g, '') === deal?.client?.phone?.replace(/[^\d]/g, '')) {
      return alert.error('Номер телефона компании и клиента совпадают');
    }

    if (values?.contactPersonPhone?.replace(/[^\d]/g, '') === deal?.client?.phone?.replace(/[^\d]/g, '')) {
      return alert.error('Номер телефона контактного лица и клиента совпадают');
    }
    if (values?.contactPersonPhone2?.replace(/[^\d]/g, '') === deal?.client?.phone?.replace(/[^\d]/g, '')) {
      return alert.error('Номер телефона контактного лица и клиента совпадают');
    }
    setLoading(true);
    try {
      const contactPersons = [];

      if (values?.contactPersonLastName) {
        contactPersons?.push({
          contactPersonLastName: values?.contactPersonLastName,
          contactPersonFirstName: values?.contactPersonFirstName,
          contactPersonPhone: values?.contactPersonPhone?.replace(/[^\d]/g, ''),
          contactPersonType: values?.contactPersonType?.value,
          residentBool: values?.residentBool?.value,
          documentGivedBy:
            String(values?.documentGivedBy?.value || values?.documentGivedBy) === '1'
              ? 'MINISTRY_OF_THE_INTERIOR'
              : 'MINISTRY_OF_JUSTICE',
          // jobTitle: values?.jobTitle,
        });
      }
      if (values?.contactPersonLastName2) {
        contactPersons?.push({
          contactPersonLastName: values?.contactPersonLastName2,
          contactPersonFirstName: values?.contactPersonFirstName2,
          contactPersonPhone: values?.contactPersonPhone2?.replace(/[^\d]/g, ''),
          contactPersonType: values?.contactPersonType2?.value,
          residentBool: values?.residentBool2?.value,
          documentGivedBy:
            String(values?.documentGivedBy2?.value || values?.documentGivedBy2) === '1'
              ? 'MINISTRY_OF_THE_INTERIOR'
              : 'MINISTRY_OF_JUSTICE',
          // jobTitle: values?.jobTitle2,
        });
      }

      const payload = {
        ...values,
        ...repaymentPayload(values),
        initialFee: values?.initialFee ? +values?.initialFee?.replace(/\s/g, '') : 0,
        incomeMain: +values?.incomeMain?.replace(/\s/g, ''),
        // IncomeAddConfirmed: +values?.IncomeAddConfirmed?.replace(/\s/g, ''),
        // familyStatus: values?.familyStatus?.value,
        period: +values?.period?.value,
        graceDuration: +values?.period?.value,
        grace,
        // incomeConfirmation: !incomeConfirmation,
        orderIds: orders?.map((item) => item?.value),
        dealId: deal?.id,
        chunkId: deal?.chunkId,
        workStatus: values?.workStatus?.label,
        workStatusId: values?.workStatus?.value,
        subsidy,
        contactPersons: contactPersons,
        bankIds: selectedBanks,
        vehicle_status: vehicle_status ? 3 : 1,
        initialRegistration: vehicle_status,
        childrensCount: values?.childrensCount || 0,
        app: emulateMobileApp,
        // kaskoInsuranceRate: values?.kaskoInsuranceRate?.value,
        // lifeInsuranceRate: values?.lifeInsuranceRate?.value,
        benefit,
      };

      // if (values?.kaskoInsuranceRate?.value) {
      //   payload.kaskoInsuranceRate = values?.kaskoInsuranceRate?.value;
      // } else {
      //   delete payload.kaskoInsuranceRate;
      // }
      // if (values?.lifeInsuranceRate?.value) {
      //   payload.lifeInsuranceRate = values?.lifeInsuranceRate?.value;
      // } else {
      //   delete payload.lifeInsuranceRate;
      // }

      //sapa temp
      if(payload.subsidy) {
        payload.lifeInsuranceRate = '6';
      }

      setForm(payload);
      Promise.resolve(onSubmit(payload, documentsForm, files))
        // Черновик стираем только когда заявка действительно ушла в банки.
        // Раньше он стирался на любом исходе — в том числе на первом клике,
        // когда бэкенд отвечает «ОТП не подписан» и менеджер идёт отправлять
        // клиенту согласие. Обновил страницу, пока клиент подписывает, —
        // и анкету приходилось забивать заново.
        .then((result) => {
          if (shouldClearDraft(result)) removeDraft(draftStorageKey);
        })
        .finally(() => setLoading(false));
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  };

  return (
    <div style={{ marginBottom: 50 }}>
      {/* <DealCreateDocs
        title="Согласие на обработку персональных данных"
        deal={deal}
        documents={documents?.filter((item) => item?.typeId === DOCUMENT_TYPES_PERSONAL_DATA_AGREEMENT)}
        setDocumentsForm={setDocumentsForm}
        onSubmit={() => {}}
      /> */}
      <HookForm
        inputs={inputsWithDefaultValues || []}
        title="Уточнение данных клиента"
        subtitle={shortFormsEnabled() ? undefined : () => {
          return (
            <div className={styles.checkboxGroup}>
              <Checkbox
                label="Субсидия"
                name="subsidy"
                id="subsidy"
                onChange={() => setSubsidy(!subsidy)}
                value={subsidy}
              />
              <Checkbox
              label="Рассрочка"
              name="grace"
              id="grace"
              onChange={() => setGrace(!grace)}
              value={grace}
            />
            {/* <Checkbox
              label="Без подтверждения дохода"
              name="incomeConfirmation"
              id="incomeConfirmation"
              onChange={() => setincomeConfirmation(!incomeConfirmation)}
              value={incomeConfirmation}
            /> */}
              <Checkbox
                label="Первичная регистрация"
                name="vehicle_status"
                id="vehicle_status"
                onChange={() => setVehicle_status(!vehicle_status)}
                value={vehicle_status}
              />
              <Checkbox
                label="Льготное кредитование"
                name="benefit"
                id="benefit"
                onChange={() => setBenefit(!benefit)}
                value={benefit}
              />
            </div>
          );
        }}
        grid={{ display: 'grid', gap: 20 }}
        onSubmit={handleSubmit}
        onValuesChange={persistDraft}
        loading={loading || sendFormLoading}
        submitDisabled={clientDocumentMissing}
        action={'Сохранить и продолжить'}
        customDataTop={
          clientDocumentMissing
            ? () => (
                <div
                  style={{
                    padding: 16,
                    borderRadius: 8,
                    background: '#FDECEC',
                    border: '1px solid #EE7171',
                  }}
                >
                  <p style={{ ...textStyles.fieldLabel, fontWeight: '400', color: '#C6280E' }}>
                    У клиента не заполнены данные удостоверяющего документа — номер, дата
                    выдачи и срок действия. Без них банки заявку не примут. Заполните их в
                    карточке клиента и вернитесь сюда.
                  </p>
                </div>
              )
            : undefined
        }
        customData={() => {
          const { showIncome, showWork } = statusFlags();
          return (
            <div
              style={{
                display: 'grid',
                gap: 20,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <p style={textStyles.formTitle}>
                  Сканы документов
                  <Hint text="Не все банки требуют предоставления сканов документов. Если клиенту необходимы ответы от всех банков, рекомендуется загрузить все документы." />
                </p>
                <ClientUploadBlock
                  dealId={deal?.id}
                  phone={deal?.client?.phone}
                  showIncome={showIncome}
                  showWork={showWork}
                  received={clientReceived}
                onRefresh={refreshClientReceived}
                />
              </div>
              {/* <DocsInputNew
                text="Письмо-подтверждение по VVIN код"
                label="Загрузить документ"
                id="vinApprovalDocumentу"
                value={files['vinApprovalDocument']}
                onChange={(file) => attachFile(file, 'vinApprovalDocument')}
              /> */}

              <DocsInputNew
                text="УДВ"
                label="Загрузить документ"
                id="УДВ"
                value={files['UDV']}
                onChange={(file) => attachFile(file, 'UDV')}
                hint="УДВ и Паспорт РК обязательны для Bank CenterCredit и Eurasian Bank. Загрузите один из документов для подачи заявки в эти банки."
              />
              <DocsInputNew
                text="Паспорт РК"
                label="Загрузить документ"
                id="Паспорт РК"
                value={files['passportRK']}
                onChange={(file) => attachFile(file, 'passportRK')}
                hint="УДВ и Паспорт РК обязательны для Bank CenterCredit и Eurasian Bank. Загрузите один из документов для подачи заявки в эти банки."
              />
              {(showIncome || clientReceived.incomeCertificate) && (
                <DocsInputNew
                  text="Справка/Выписка дохода"
                  label="Загрузить документ"
                  id="Справка/Выписка дохода"
                  value={files['incomeCertificate']}
                  onChange={(file) => attachFile(file, 'incomeCertificate')}
                  clientFile={clientReceived.incomeCertificate}
                  onRemoveClientFile={() => removeClientFile('incomeCertificate')}
                  hint={
                    showIncome
                      ? 'Обязательный документ для подачи заявки в Eurasian Bank и Bereke Bank.'
                      : 'Для текущего статуса клиента не обязательна, но присланный клиентом файл приложится к заявке.'
                  }
                />
              )}

              {(showWork || clientReceived.workCertificate) && (
                <DocsInputNew
                  text="Выписка со счета"
                  label="Загрузить документ"
                  id="Выписка со счета"
                  value={files['workCertificate']}
                  onChange={(file) => attachFile(file, 'workCertificate')}
                  clientFile={clientReceived.workCertificate}
                  onRemoveClientFile={() => removeClientFile('workCertificate')}
                  hint={
                    showWork
                      ? undefined
                      : 'Для текущего статуса клиента не обязательна, но присланный клиентом файл приложится к заявке.'
                  }
                />
              )}
              <DocsInputNew
                text="Фотография клиента"
                label="Загрузить документ"
                id="Фотография клиента"
                value={files['clientPhoto']}
                onChange={(file) => attachFile(file, 'clientPhoto')}
                clientFile={clientReceived.clientPhoto}
                onRemoveClientFile={() => removeClientFile('clientPhoto')}
                hint="Обязательно для Bank CenterCredit и Eurasian Bank"
              />

              {/* <DocsInputNew
                text="Документ подтверждающий ИП"
                label="Загрузить документ"
                id="Документ подтверждающий ИП"
                value={files['legalConfirm']}
                onChange={(file) => attachFile(file, 'legalConfirm')}
              /> */}

              <BanksPicker
                onChange={setSelectedBanks}
                defaultValue={selectedBanks}
                allowedBankIds={usedCar ? USED_CAR_BANK_IDS : undefined}
              />

              {!IS_PROD_STAND && (
                <div style={{ border: '1px dashed #F5C56B', background: '#FFF6E5', borderRadius: 8, padding: '12px 16px' }}>
                  <Checkbox
                    label="ТЕСТ: считать заявку поданной из мобильного приложения"
                    name="emulateMobileApp"
                    id="emulateMobileApp"
                    onChange={() => setEmulateMobileApp(!emulateMobileApp)}
                    value={emulateMobileApp}
                  />
                  <p style={{ fontSize: 11, color: '#8B8B8B', marginTop: 6 }}>
                    Заявка уйдёт с флагом app=true, как из приложения: банки выберутся автоматически (список
                    мобильного приложения), выбор выше будет проигнорирован. Через 2 часа без ответа банка сделка
                    закроется сама, а автомобиль вернётся в наличие. Только для тестового стенда.
                  </p>
                </div>
              )}
            </div>
          );
        }}
        backHandler={backHandler}
      />
      <Modal
        isOpen={agreementModal}
        onRequestClose={() => setAgreementModal(false)}
        width="100%"
        contentStyle={{
          maxWidth: 600,
          height: 'max-content',
        }}
      >
        <DealCreateAgreement
          deal={deal}
          // Согласие печатается до создания заявки в банк, поэтому суммы и срока
          // там неоткуда взять — передаём их из анкеты. Без этого в бланке Форте
          // пустовали «Сумма и валюта кредита», «Срок кредита», «Семейное
          // положение» и «Количество несовершеннолетних детей».
          creditAppeal={{
            loanMoney:
              deal?.sum && form?.initialFee
                ? deal.sum - Number(String(form.initialFee).replace(/\s/g, ''))
                : deal?.sum,
            // В form лежит уже нормализованная анкета, где period — число,
            // а не выбранный вариант списка. Поэтому берём оба вида.
            period: form?.period?.value ?? form?.period,
            MaritalStatus: form?.MaritalStatus?.label || form?.MaritalStatus?.value,
            childrensCount: form?.childrensCount || 0,
          }}
          sendAppeal={() => onSubmit(form, documentsForm, files, true)}
          onClose={() => {
            setAgreementModal(false);
          }}
          sendFormLoading={sendFormLoading}
          bankIds={selectedBanks}
        />
      </Modal>
    </div>
  );
};

export default DealCreateCreditAppeal;
